import { eachDayOfInterval, format, formatISO } from "date-fns";

import { datetimeToJS } from "@/helpers/parsers";
import { cn } from "@/lib/utils";

import { UnavailableIcon } from "./icons";
import Moon from "./moon";
import type { DateRangeType, ParsedTimeslotType, TimeslotType } from "./types";

interface TimeslotItemProps extends React.ComponentProps<"div"> {}

function TimeslotItem({
  children = <UnavailableIcon />,
  className,
  ...props
}: TimeslotItemProps) {
  return (
    <div
      className={cn(
        "relative flex h-9 flex-col items-center justify-center rounded-[4px] border border-cool-gray/2 bg-white/2 shadow-0-0-4 shadow-black/10 transition-[colors,shadow]",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

interface TimeslotProps {
  isSelected: boolean;
  onClick(id: string): void;
  timeslot: ParsedTimeslotType | null;
}

function Timeslot({ isSelected, onClick, timeslot }: TimeslotProps) {
  const { available = false, date } = timeslot || {};

  const props: TimeslotItemProps = {};

  if (isSelected) {
    props.className = "bg-white border-white cursor-pointer";
    props.onClick = () => onClick(timeslot?.id ?? "");
  } else if (available) {
    props.className = cn(
      "cursor-pointer border-cool-gray/70 bg-white/5",
      "shadow-white/20 hover:border-dark-silver hover:bg-white/10 hover:shadow-0-0-10"
    );
    props.onClick = () => onClick(timeslot?.id ?? "");
  }

  return (
    <TimeslotItem {...props}>
      {available || isSelected ? (
        <>
          <span
            className={cn(
              "text-13/[1em]",
              isSelected ? "text-black" : "text-white"
            )}
          >
            {format(date ?? new Date(), "h:mm")}
          </span>
          <span className="text-11/[1em] text-battleship-gray">
            {format(date ?? new Date(), "aaa")}
          </span>
        </>
      ) : (
        <UnavailableIcon />
      )}
      {!!date && <Moon date={date} />}
    </TimeslotItem>
  );
}

interface DayProps {
  date: Date;
  onClick(id: string): void;
  timeslotId?: string;
  timeslots: Array<ParsedTimeslotType | null>;
}

function Day({ date, onClick, timeslotId, timeslots }: DayProps) {
  return (
    <div className="flex flex-col items-stretch gap-[5px]">
      <div className="flex h-[59px] flex-col justify-center leading-[1.42em]">
        <p className="font-medium text-11 text-cool-gray">
          {format(date, "E")}
        </p>
        <p className="font-medium text-16 text-white">{format(date, "d")}</p>
        <p className="font-medium text-11 text-cool-gray">
          {format(date, "MMM")}
        </p>
      </div>
      {timeslots.map((timeslot, index) => (
        <Timeslot
          isSelected={!!timeslotId && timeslot?.id === timeslotId}
          key={timeslot?.date.getTime() || index}
          onClick={onClick}
          timeslot={timeslot}
        />
      ))}
    </div>
  );
}

const MAX_TIMESLOTS = 6;

interface WeekViewProps {
  dateRange: DateRangeType;
  ignoreTimezone: boolean;
  onClick(id: string): void;
  timeslotId?: string;
  timeslots: TimeslotType[];
}

export default function WeekView({
  dateRange,
  ignoreTimezone,
  onClick,
  timeslotId,
  timeslots,
}: WeekViewProps) {
  const isEmptyRow = (ts: Array<ParsedTimeslotType | null>[], index: number) =>
    ts.every((dts) => !dts[index]?.available);

  const findFirstEmptyRowIndex = (ts: Array<ParsedTimeslotType | null>[]) =>
    ts[0].findIndex((_, index) => isEmptyRow(ts, index));

  const removeRow = (
    ts: Record<string, Array<ParsedTimeslotType | null>>,
    index: number
  ) =>
    Object.fromEntries(
      Object.entries(ts).map(([date, dts]) => [
        date,
        dts.filter((_, rIndex) => rIndex !== index),
      ])
    );

  let groupedTimeslots = timeslots.reduce(
    (accum, timeslot) => {
      const [date] = timeslot.datetime.split("T");

      if (!(date in accum)) {
        accum[date] = [];
      }

      accum[date].push({
        available: timeslot.available,
        date: datetimeToJS(timeslot.datetime, ignoreTimezone),
        id: timeslot.id,
      });

      return accum;
    },
    {} as Record<string, Array<ParsedTimeslotType | null>>
  );
  const timeslotsCount = Object.values(groupedTimeslots).reduce(
    (accum, timeslots) => Math.max(accum, timeslots.length),
    0
  );

  const days = eachDayOfInterval({
    end: dateRange.dateLte,
    start: dateRange.dateGte,
  });

  groupedTimeslots = Object.fromEntries(
    days.map((day) => {
      const date = formatISO(day, { representation: "date" });
      const dayTimeslots = groupedTimeslots[date] || [];
      const timeslotsToAdd = new Array(
        timeslotsCount - dayTimeslots.length
      ).fill(null);

      return [date, [...timeslotsToAdd, ...dayTimeslots]];
    })
  );

  const rowsToRemove = Math.max(timeslotsCount - MAX_TIMESLOTS, 0);

  for (let c = 0; c < rowsToRemove; c++) {
    const rowIndex = findFirstEmptyRowIndex(Object.values(groupedTimeslots));

    if (rowIndex !== -1) {
      groupedTimeslots = removeRow(groupedTimeslots, rowIndex);
    }
  }

  return (
    <div className="mt-[5px] grid grid-cols-7 gap-1">
      {days.map((d) => {
        const date = formatISO(d, { representation: "date" });
        return (
          <Day
            date={d}
            key={date}
            onClick={onClick}
            timeslotId={timeslotId}
            timeslots={groupedTimeslots[date] || []}
          />
        );
      })}
    </div>
  );
}
